// Deterministic repair-vs-replace scorer — the TS mirror of
// crates/hvaccollector/src/compose.rs (score_wizard). The Rust side is the
// authority for what gets relayed; this copy exists for instant client-side
// display. Weights live in the tree contract (`points`, `years`); only the
// two formula rules below live in code. Keep all three in sync.
import type { Answer, DecisionTree } from '@/shared/diagnostic/types'

export const TYPICAL_REPLACEMENT_USD = 9000

export type VerdictBand = 'REPAIR' | 'EITHER' | 'REPLACE'

export interface VerdictFactor {
    label: string
    points: number
    note?: string
}

export interface WizardVerdict {
    score: number
    band: VerdictBand
    factors: VerdictFactor[]
    quote: number
}

export function scoreWizard(tree: DecisionTree, answers: Answer[]): WizardVerdict {
    let score = 0
    const factors: VerdictFactor[] = []

    for (const answer of answers) {
        const node = tree.nodes[answer.node_id]
        if (!node) continue

        if (node.kind === 'single_select' && typeof answer.value === 'string') {
            const opt = node.options?.find((o) => o.value === answer.value)
            if (opt?.points) {
                score += opt.points
                factors.push({ label: opt.label, points: opt.points })
            }
        } else if (node.kind === 'multi_select' && Array.isArray(answer.value)) {
            for (const v of answer.value) {
                const opt = node.options?.find((o) => o.value === v)
                if (opt?.points) {
                    score += opt.points
                    factors.push({ label: opt.label, points: opt.points })
                }
            }
        }
    }

    const quote =
        (answers.find((a) => a.node_id === 'repair_quote')?.value as number | undefined) ?? 0
    const ageOpt = tree.nodes['age_band']?.options?.find(
        (o) => o.value === answers.find((a) => a.node_id === 'age_band')?.value,
    )

    // The $5,000 rule: age x repair cost > $5,000 leans replace.
    if (ageOpt?.years !== undefined) {
        const product = ageOpt.years * quote
        if (product > 5000) {
            score += 15
            factors.push({
                label: 'The $5,000 rule',
                points: 15,
                note: `${ageOpt.years} yrs × $${quote.toLocaleString()} = $${product.toLocaleString()}`,
            })
        }
    }

    // The 50% rule: quote above half a typical replacement leans replace.
    if (quote > TYPICAL_REPLACEMENT_USD * 0.5) {
        score += 10
        factors.push({
            label: 'The 50% rule',
            points: 10,
            note: `$${quote.toLocaleString()} exceeds half of a $${TYPICAL_REPLACEMENT_USD.toLocaleString()} typical replacement`,
        })
    }

    score = Math.min(Math.max(score, 0), 100)
    const band: VerdictBand = score <= 39 ? 'REPAIR' : score <= 59 ? 'EITHER' : 'REPLACE'
    return { score, band, factors, quote }
}
